import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Save, Settings2 } from 'lucide-react'

import type { LotSizeStage } from '@/lib/api/lot-size-api'
import { RefreshButton } from '@/components/common/refresh-button'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useLotSizeSettings,
    useUpdateLotSize,
} from '@/hooks/use-lot-size-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/settings/lot-sizes',
)({
    component: LotSizesPage,
})

const STAGE_LABEL: Record<LotSizeStage, () => string> = {
    purchase: () => m.lot_size_stage_purchase(),
    sorting: () => m.lot_size_stage_sorting(),
    scenting: () => m.lot_size_stage_scenting(),
}

function LotSizesPage() {
    const { data = [], isLoading, isFetching, refetch } = useLotSizeSettings()
    const update = useUpdateLotSize()

    const [drafts, setDrafts] = useState<Record<string, number>>({})

    useEffect(() => {
        // Sync drafts khi data load lần đầu — chỉ set field nào chưa có trong drafts.
        setDrafts((prev) => {
            const next = { ...prev }
            for (const row of data) {
                if (next[row.stage] === undefined) {
                    next[row.stage] = row.default_kg
                }
            }
            return next
        })
    }, [data])

    const handleSave = async (stage: LotSizeStage) => {
        const kg = drafts[stage]
        if (!kg || kg <= 0) return
        await update.mutateAsync({ stage, defaultKg: kg })
    }

    return (
        <div className="space-y-4 px-1 py-2 md:p-6">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Settings2 className="size-6 text-primary" />
                    <h1 className="text-2xl font-semibold">
                        {m.lot_size_title()}
                    </h1>
                </div>
                <RefreshButton
                    onClick={() => void refetch()}
                    loading={isFetching}
                />
            </div>
            <p className="text-sm text-muted-foreground">
                {m.lot_size_subtitle()}
            </p>

            <Card>
                <CardHeader>
                    <CardTitle>{m.lot_size_title()}</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-1/3">
                                    {m.tea_purchase_tea_state()}
                                </TableHead>
                                <TableHead className="w-1/3">
                                    {m.lot_size_default_kg()}
                                </TableHead>
                                <TableHead>
                                    {m.lot_size_updated_by({ name: '' })}
                                </TableHead>
                                <TableHead className="text-right" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={4}
                                        className="py-8 text-center text-muted-foreground"
                                    >
                                        {m.common_loading()}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                data.map((row) => (
                                    <TableRow key={row.stage}>
                                        <TableCell className="font-medium">
                                            {STAGE_LABEL[row.stage]()}
                                        </TableCell>
                                        <TableCell>
                                            <Label
                                                htmlFor={`kg-${row.stage}`}
                                                className="sr-only"
                                            >
                                                {m.lot_size_default_kg()}
                                            </Label>
                                            <Input
                                                id={`kg-${row.stage}`}
                                                type="number"
                                                min={0.1}
                                                step="0.1"
                                                value={
                                                    drafts[row.stage] ??
                                                    row.default_kg
                                                }
                                                onChange={(e) =>
                                                    setDrafts((prev) => ({
                                                        ...prev,
                                                        [row.stage]: Number(
                                                            e.target.value,
                                                        ),
                                                    }))
                                                }
                                                className="w-32"
                                            />
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {row.updated_by_name || '—'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                size="sm"
                                                disabled={
                                                    update.isPending ||
                                                    (drafts[row.stage] ??
                                                        row.default_kg) ===
                                                        row.default_kg
                                                }
                                                onClick={() =>
                                                    handleSave(row.stage)
                                                }
                                            >
                                                <Save className="size-4 mr-1" />
                                                {m.lot_size_save()}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    )
}
